import React, { useCallback, useEffect, useMemo, useState } from "react";
import InputV2 from "@ui/Input/InputV2";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import { short, veryLong } from "diagnostics-lib/io";
import { isLeft } from "fp-ts/lib/Either";
import useInput from "@ui/lib/hooks/useInput";
import t from "../../../../../i18n";
import * as io from "io-ts";
import { useSettings } from "../../../../../api/useSettings";
import type { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import Select from "@ui/Select";
import { getStatusOptions, type DenormalizedEventStatus } from "../../utils/faultEventStatus";
import Text from "@ui/Text";
import RadioButton from "@ui/RadioButton";
import { color, fonts } from "@ui";
import Field from "@ui/Form/Field";
import TextareaInput from "@ui/TextareaInput";
import { useManualFailures } from "../../../../../api/useManualFailures";
import type { Project } from "../../../../../api/useProjects";
import { indexBy } from "diagnostics-lib/iterable";
import { useCategories } from "../../../../../api/useCategories";
import Button from "@ui/Button";
import tui from "@ui/i18n";
import { buildOptions, NONE_LABEL } from "@ui/Filter";
import { reparabilities, type Reparability } from "../../../../../api/Reparability";
import { useTranslations } from "../../../../../api/useTranslations";
import { Markdown } from "@ui/Markdown";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import classNames from "classnames";
import { useProjectPeriod } from "../../../useProjectPeriod";
import { useProjectSource } from "../../../../../api/useSources";
import { parseAbsolute, toCalendarDate, type DateValue } from "@internationalized/date";
import { DatePicker } from "@ui/DatePicker";
import formatDate from "@ui/i18n/formatDate";
import useToasts from "@ui/Toast/useToasts";
import { hasManualFaultEventOverlap } from "diagnostics-domain/manualFaultEvent";
import { useRemoteManualFaultEvents } from "../../../../../api/useManualFaultEvents";
import { usePost } from "../../../../../api/useCall";
import { AlertFill, InformationLine } from "@ui/IconV2/react";
import { useNavigate } from "react-router";
import { toPath } from "../../../../../router/helpers/path";
import { useQuantities } from "../../../../../api/useQuantities";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { EVENT_PRIORITIES, getEventPriorityText } from "../../utils/event";
import SignedNumberInput from "../../views/Event/ManualFaultEvent/components/SignedNumberInput";

export interface DrawerCreateManualFaultEventRef {
  resetForm: () => void;
}
interface DrawerCreateManualFaultEventProps {
  project: Project;
  onCloseDrawer: () => void;
}

const DrawerCreateManualFaultEvent = React.forwardRef<
  DrawerCreateManualFaultEventRef,
  DrawerCreateManualFaultEventProps
>(({ project, onCloseDrawer }, ref) => {
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const settings = useSettings();
  const translations = useTranslations();
  const source = useProjectSource(project);
  const quantities = useQuantities(project.id);
  const indicatorPerformanceQuantities = useMemo(
    () =>
      quantities
        .filter(q => q.qoiOrder !== null)
        .sort((a, b) => (a.qoiOrder ?? 0) - (b.qoiOrder ?? 0)),
    [quantities],
  );
  const manualFailures = useManualFailures(project.id);
  const indexedManualFailures = React.useMemo(
    () => indexBy(manualFailures, "id"),
    [manualFailures],
  );
  const systems = useCategories("SYSTEM");
  const remoteManualFaultEvents = useRemoteManualFaultEvents(
    project.id,
    settings.MANUAL_EVENTS_DEV_MODE_ENABLED,
  );
  const createManualFaultEvent = usePost("/projects/:projectId(uuid)/manualFaultEvent");
  if (!source) throw new Error("No source found for the project");

  // Name
  const { value: nameInput, dirty: dirtyNameInput, setValue: setNameInput } = useInput("");
  const errorNameInput: string | undefined = React.useMemo(() => {
    if (!dirtyNameInput) return;

    const trimNameInput = nameInput.trim();
    if (!trimNameInput)
      return t("project.fault_event.fault.hearder.editing.name.error.cannot_be_empty");
    if (isLeft(short(io.string).decode(trimNameInput))) {
      return t("project.fault_event.fault.hearder.editing.name.error.cannot_exceed_100_characters");
    }
    return;
  }, [nameInput, dirtyNameInput]);

  // Date Picker
  const [, , projectExtremes] = useProjectPeriod({ source, project, boundaries: "analyses" });
  const latestActiveAnalysisDate = React.useMemo(() => {
    const latestActiveAnalysis = project.analysesAggregate?.latestActiveAnalysis?.date;
    if (!latestActiveAnalysis) return null;

    const latestDate = new Date(latestActiveAnalysis);
    latestDate.setHours(23, 59, 59, 999);
    return latestDate;
  }, [project.analysesAggregate?.latestActiveAnalysis?.date]);
  const disableDatePickers = latestActiveAnalysisDate === null;

  const dateBoundaries = React.useMemo(() => {
    const startDate = new Date(projectExtremes.from);
    startDate.setHours(0, 0, 0, 0);

    const endDate = latestActiveAnalysisDate
      ? new Date(latestActiveAnalysisDate)
      : new Date(projectExtremes.from);
    endDate.setHours(23, 59, 59, 999);

    return {
      startDate,
      endDate,
      startDateValue: parseAbsolute(startDate.toISOString(), project.timezone),
      endDateValue: parseAbsolute(endDate.toISOString(), project.timezone),
    };
  }, [latestActiveAnalysisDate, projectExtremes.from, project.timezone]);

  // Date Picker startsAt
  const [startsAt, setStartsAt] = React.useState<DateValue | null>(null);
  const onStartsAtDatePickerChange = React.useCallback(
    (value: DateValue | null) => {
      const resValue = value;
      if (resValue === null) {
        setStartsAt(null);
      } else {
        resValue.set({
          hour: 0,
          minute: 0,
          second: 0,
        });
        const date = toCalendarDate(resValue).toDate(project.timezone);
        setStartsAt(parseAbsolute(date.toISOString(), project.timezone));
      }
    },
    [project.timezone],
  );

  // Date Picker closedAt
  const [closedAt, setClosedAt] = React.useState<DateValue | null>(null);
  const onClosedAtDatePickerChange = React.useCallback(
    (value: DateValue | null) => {
      const resValue = value;
      if (resValue === null) {
        setClosedAt(null);
      } else {
        resValue.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
        const date = toCalendarDate(resValue).toDate(project.timezone);
        setClosedAt(parseAbsolute(date.toISOString(), project.timezone));
      }
    },
    [project.timezone],
  );

  const errorStartedDate = React.useMemo(() => {
    if (startsAt === null) return;

    if (startsAt < dateBoundaries.startDateValue || startsAt > dateBoundaries.endDateValue)
      return t("project.events.drawer.create_manual_event.start.error");

    return;
  }, [startsAt, dateBoundaries.startDateValue, dateBoundaries.endDateValue]);

  const errorClosedDate = React.useMemo(() => {
    if (closedAt === null || errorStartedDate) return;

    if (
      closedAt < dateBoundaries.startDateValue ||
      (startsAt === null ? closedAt > dateBoundaries.endDateValue : closedAt < startsAt)
    )
      return t("project.events.drawer.create_manual_event.closed.error");

    if (closedAt > dateBoundaries.endDateValue)
      return t("project.events.drawer.create_manual_event.closed.start.error");

    return;
  }, [
    errorStartedDate,
    closedAt,
    startsAt,
    dateBoundaries.startDateValue,
    dateBoundaries.endDateValue,
  ]);

  // Description
  const {
    value: descriptionInput,
    dirty: dirtyDescriptionInput,
    onChange: handlerDescriptionInput,
    setValue: setDescriptionInput,
  } = useInput("");
  const errorDescriptionInput: string | undefined = React.useMemo(() => {
    if (!dirtyDescriptionInput) return;

    const trimDescriptionInput = descriptionInput.trim();
    if (isLeft(veryLong(io.string).decode(trimDescriptionInput))) {
      return t("project.events.drawer.create_manual_event.description.error.max_characters");
    }
    return;
  }, [descriptionInput, dirtyDescriptionInput]);

  // Priority
  const disableMediumPriority = settings.FAULT_EVENTS_DISABLE_MEDIUM_PRIORITY;
  const [priority, setPriority] = React.useState<PriorityFaultEvent>("LOW");
  const priorityOptions = React.useMemo(
    () =>
      EVENT_PRIORITIES.filter(priority => !disableMediumPriority || priority !== "MEDIUM").map(
        priority => ({
          label: getEventPriorityText(priority),
          value: priority,
        }),
      ),
    [disableMediumPriority],
  );

  // Status
  const defaultStatus: DenormalizedEventStatus = "ONGOING";
  const [status, setStatus] = React.useState<DenormalizedEventStatus>(defaultStatus);
  const statusOptions = React.useMemo(() => getStatusOptions(defaultStatus), []);

  // Repair Category
  const {
    value: repairCategory,
    onChange: onChangeRepairCategory,
    setValue: setRepairCategory,
  } = useInput(NONE_LABEL);
  const repairCategoryOptions = React.useMemo(
    () =>
      [{ label: NONE_LABEL, value: NONE_LABEL }].concat(
        buildOptions(
          reparabilities.map(v => ({
            label: t("reparability", v, translations),
            value: v,
          })),
        ),
      ),
    [translations],
  );
  const canEditRepairCategory = status === "ACTION_IDENTIFIED" || status === "CLOSED";

  // Action
  const [identifiedAction, setIdentifiedAction] = React.useState("");
  const errorIdentifiedAction = React.useMemo(() => {
    if (isLeft(veryLong(io.string).decode(identifiedAction))) {
      return t("project.fault_event.modal.form.change_status.input.action_identified.error");
    }
    return;
  }, [identifiedAction]);

  // Anomaly
  const manualFailureOptions = React.useMemo(
    () =>
      manualFailures
        .map(mf => ({
          label: mf.name,
          value: mf.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [manualFailures],
  );
  const systemOptions = React.useMemo(
    () =>
      systems.map(systemId => ({
        label: systemId,
        value: systemId,
      })),
    [systems],
  );

  const [isCreatingNewAnomaly, setIsCreatingNewAnomaly] = React.useState<boolean>(true);
  const [manualFaultName, setManualFaultName] = React.useState<string>(nameInput.trim());
  const [system, setSystem] = React.useState<string>("");

  const errorCreatingAnomaly = React.useMemo(() => {
    if (!isCreatingNewAnomaly || !manualFaultName) return false;
    return manualFailures.some(mf => mf.name === nameInput.trim());
  }, [isCreatingNewAnomaly, manualFailures, nameInput, manualFaultName]);

  const errorOverlappingManualFaultEvent = React.useMemo(() => {
    if ((isCreatingNewAnomaly && !dirtyNameInput) || startsAt === null || !manualFaultName)
      return false;

    const manualFaultEventDBs = remoteManualFaultEvents.map(mfe => {
      const name = indexedManualFailures.get(mfe.manualFaultId)?.name ?? mfe.manualFaultId;
      return {
        manualFaultName: name ?? "",
        startsAt: new Date(mfe.startsAt),
        closedAt: mfe.closedAt ? new Date(mfe.closedAt) : null,
      };
    });

    const startsAt_ = toCalendarDate(startsAt).toDate(project.timezone);
    const closedAt_ = closedAt ? toCalendarDate(closedAt).toDate(project.timezone) : null;
    const newManualFaultEvent = {
      manualFaultName,
      startsAt: startsAt_,
      closedAt: closedAt_,
    };
    const isOverlapping = hasManualFaultEventOverlap(manualFaultEventDBs, newManualFaultEvent);
    return isOverlapping;
  }, [
    isCreatingNewAnomaly,
    dirtyNameInput,
    startsAt,
    closedAt,
    remoteManualFaultEvents,
    manualFaultName,
    indexedManualFailures,
    project.timezone,
  ]);

  // Impacts
  const [impactValues, setImpactValues] = useState<Record<string, string>>({});
  useEffect(() => {
    setImpactValues(prev => {
      const newValues: Record<string, string> = {};

      for (const q of indicatorPerformanceQuantities) {
        newValues[q.id] = prev[q.id] ?? "0";
      }

      return newValues;
    });
  }, [indicatorPerformanceQuantities]);

  // Reset
  const resetForm = React.useCallback(() => {
    setNameInput("");
    setStartsAt(null);
    setClosedAt(null);
    setDescriptionInput("");
    setPriority("LOW");
    setStatus(defaultStatus);
    setRepairCategory(NONE_LABEL);
    setIdentifiedAction("");
    setIsCreatingNewAnomaly(false);
    setManualFaultName("");
    setSystem("");
  }, [
    setNameInput,
    setStartsAt,
    setClosedAt,
    setDescriptionInput,
    setPriority,
    defaultStatus,
    setStatus,
    setRepairCategory,
    setIdentifiedAction,
    setIsCreatingNewAnomaly,
    setManualFaultName,
    setSystem,
  ]);
  React.useImperativeHandle(ref, () => ({
    resetForm,
  }));

  const handleSubmit = useCallback(async () => {
    try {
      const name_ = nameInput.trim();
      if (!startsAt || !name_) return;

      const startsAt_ = toCalendarDate(startsAt).toDate(project.timezone);

      const identifiedActionAt_ = status === "ACTION_IDENTIFIED" ? new Date() : undefined;
      const closedAt_ = closedAt ? toCalendarDate(closedAt).toDate(project.timezone) : undefined;
      const description_ = descriptionInput.trim() || undefined;
      const identifiedAction_ = identifiedAction.trim() || undefined;
      const reparability_ =
        repairCategory === NONE_LABEL ? undefined : (repairCategory as Reparability);

      const manualFaultEvent = {
        name: name_,
        startsAt: startsAt_,
        priority,
        closedAt: closedAt_,
        description: description_,
        identifiedActionAt: identifiedActionAt_,
        identifiedAction: identifiedAction_,
        reparability: reparability_,
      };

      const manualFaultId_ = isCreatingNewAnomaly
        ? undefined
        : manualFailures.find(mf => mf.name === manualFaultName)?.id;
      const manualFaultName_ = isCreatingNewAnomaly ? manualFaultName.trim() : undefined;
      const systemId_ = system || null;

      let manualFault:
        | { associatedManualFault: { id: string } }
        | {
            createManualFault: { name: string; systemId: string | null };
          };
      if (isCreatingNewAnomaly) {
        if (!manualFaultName_)
          throw new Error("ManualFaultName is required when creating a new anomaly.");
        manualFault = {
          createManualFault: {
            name: manualFaultName_,
            systemId: systemId_,
          },
        };
      } else {
        if (!manualFaultId_)
          throw new Error("manualFaultId could not be found from manualFailures.");
        manualFault = {
          associatedManualFault: { id: manualFaultId_ },
        };
      }

      const manualFaultImpacts = Object.entries(impactValues).map(([id, value]) => ({
        quantityId: id,
        value: Number(value),
      }));

      const res = await createManualFaultEvent({
        routeParams: { projectId: project.id },
        body: {
          manualFaultEvent,
          manualFault,
          manualFaultImpacts,
        },
      });
      addToast(
        "success",
        <Text>
          {t(
            "project.events.drawer.create_manual_event.toast.success",
            <Text type="body1SemiBold" as="span">
              {nameInput}
            </Text>,
          )}
        </Text>,
      );
      void navigate(
        toPath("/projects/:projectId/fault-events/manual/:manualFaultEventId/fault", {
          projectId: project.id,
          manualFaultEventId: res.body.id,
        }),
      );
      onCloseDrawer();
    } catch (error) {
      addToast("error", t("errors.backend_error"));
    }
  }, [
    addToast,
    navigate,
    createManualFaultEvent,
    onCloseDrawer,
    project.id,
    project.timezone,
    nameInput,
    startsAt,
    closedAt,
    status,
    descriptionInput,
    priority,
    repairCategory,
    identifiedAction,
    isCreatingNewAnomaly,
    manualFaultName,
    manualFailures,
    system,
    impactValues,
  ]);

  const isSubmittable =
    dirtyNameInput &&
    !errorNameInput &&
    startsAt !== null &&
    !disableDatePickers &&
    !errorStartedDate &&
    !errorClosedDate &&
    !errorCreatingAnomaly &&
    !errorOverlappingManualFaultEvent &&
    ((!isCreatingNewAnomaly && manualFaultName) || (isCreatingNewAnomaly && manualFaultName));

  return (
    <div className={drawer}>
      <div className={drawer_form}>
        <div className={section}>
          <InputV2
            className={fieldWidth}
            label={t("project.events.drawer.create_manual_event.input.name")}
            placeholder=""
            value={nameInput}
            onChange={e => {
              const name = e.target.value;
              setNameInput(name);
              if (isCreatingNewAnomaly) setManualFaultName(name);
            }}
            error={errorNameInput}
            required
          />

          <DatePicker
            id=""
            label={t("project.events.drawer.create_manual_event.date_picker.startsAt.label")}
            dateOptions={{
              shouldCloseOnSelect: true,
              hideTimeZone: true,
              granularity: "day",
              defaultValue: dateBoundaries.startDateValue,
              minValue: dateBoundaries.startDateValue,
              maxValue: dateBoundaries.endDateValue,
              value: startsAt,
              isDateUnavailable: date => {
                if (
                  dateBoundaries.startDateValue &&
                  date.compare(dateBoundaries.startDateValue) < 0
                )
                  return true;
                if (dateBoundaries.endDateValue && date.compare(dateBoundaries.endDateValue) > 0)
                  return true;
                return false;
              },
              onChange: onStartsAtDatePickerChange,
            }}
            error={errorStartedDate}
            disabled={
              disableDatePickers ||
              formatDate(projectExtremes.from, "slash", project.timezone) ===
                formatDate(projectExtremes.to, "slash", project.timezone)
            }
            required
          />

          <Field id="" label="Description" className={field}>
            <TextareaInput
              value={descriptionInput}
              onChange={handlerDescriptionInput}
              error={errorDescriptionInput}
              minHeight={120}
              enforceMaxLength={false}
              showCharCounter={false}
            />
          </Field>

          <Select
            className={fieldWidth}
            label={t("project.events.drawer.create_manual_event.select.priority")}
            prompt=""
            value={priority}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setPriority(e.target.value as PriorityFaultEvent)
            }
            options={priorityOptions}
            clearable={false}
          />

          <Select
            className={fieldWidth}
            label={t("project.events.drawer.create_manual_event.select.status")}
            prompt=""
            value={status}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setStatus(e.target.value as DenormalizedEventStatus)
            }
            options={statusOptions}
            clearable={false}
          />

          {status === "CLOSED" && (
            <DatePicker
              id=""
              label={t("project.events.drawer.create_manual_event.date_picker.closedAt.label")}
              dateOptions={{
                shouldCloseOnSelect: true,
                hideTimeZone: true,
                granularity: "day",
                minValue: startsAt ?? dateBoundaries.startDateValue,
                maxValue: dateBoundaries.endDateValue,
                value: closedAt,
                isDateUnavailable: date => {
                  const startDate = startsAt ?? dateBoundaries.startDateValue;
                  if (startDate && date.compare(startDate) < 0) return true;
                  if (dateBoundaries.endDateValue && date.compare(dateBoundaries.endDateValue) > 0)
                    return true;
                  return false;
                },
                onChange: onClosedAtDatePickerChange,
              }}
              error={errorClosedDate}
              disabled={
                disableDatePickers ||
                formatDate(projectExtremes.from, "slash", project.timezone) ===
                  formatDate(projectExtremes.to, "slash", project.timezone)
              }
              required
            />
          )}

          {canEditRepairCategory && (
            <Select
              className={fieldWidth}
              label={t("project.fault_event.modal_form.select.outage_type_for_repair.label")}
              prompt=""
              value={repairCategory}
              onChange={onChangeRepairCategory}
              options={repairCategoryOptions}
              clearable={false}
            />
          )}

          {status === "ACTION_IDENTIFIED" && (
            <div className={markdownWrapper}>
              <Text type="body1SemiBold">
                {t("project.fault_event.modal.form.change_status.input.action_identified.label")}
              </Text>
              <Markdown
                editable
                autoFocus={false}
                defaultValue={identifiedAction}
                onConversion={m => setIdentifiedAction(m.trim())}
                placeholder={t(
                  "project.fault_event.modal.form.change_status.input.action_identified.placeholder",
                )}
                className={classNames(markdownEditorError, { error: errorIdentifiedAction })}
              />
              {errorIdentifiedAction && (
                <AdditionnalMessage
                  className={markdownError}
                  status="ERROR"
                  text={errorIdentifiedAction}
                />
              )}
            </div>
          )}
        </div>

        <div className={section}>
          <div className={section_anomaly_header}>
            <Text type="body1SemiBold">
              {t("project.events.drawer.create_manual_event.section.anomaly.title")}
            </Text>
            <Text type="body2">
              {t("project.events.drawer.create_manual_event.section.anomaly.description")}
            </Text>
          </div>
          <div className={radioGroup}>
            <RadioButton
              checked={isCreatingNewAnomaly}
              onChange={() => {
                setIsCreatingNewAnomaly(true);
                setManualFaultName(nameInput.trim());
                setSystem("");
              }}
            >
              {t("project.events.drawer.create_manual_event.section.anomaly.radio.label.no")}
            </RadioButton>
            <RadioButton
              checked={!isCreatingNewAnomaly}
              onChange={() => {
                setIsCreatingNewAnomaly(false);
                setManualFaultName("");
                setSystem("");
              }}
              disabled={manualFailures.length === 0}
            >
              {t("project.events.drawer.create_manual_event.section.anomaly.radio.label.yes")}
            </RadioButton>
          </div>
          {isCreatingNewAnomaly ? (
            <>
              <Select
                className={fieldWidth}
                label={t(
                  "project.events.drawer.create_manual_event.section.anomaly.select.system.label",
                )}
                prompt={t("common.select")}
                value={system ?? undefined}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSystem(e.target.value)}
                options={systemOptions}
              />
            </>
          ) : (
            <>
              <Select
                className={fieldWidth}
                label={t("project.events.drawer.create_manual_event.section.anomaly.select.label")}
                prompt={t("common.select")}
                value={manualFaultName}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setManualFaultName(e.target.value);
                  const systemId =
                    manualFailures.find(mf => mf.name === e.target.value)?.systemId ?? "";
                  setSystem(systemId);
                }}
                options={manualFailureOptions}
              />
              {system && (
                <Field
                  id=""
                  label={t(
                    "project.events.drawer.create_manual_event.section.anomaly.select.impacted_system.label",
                  )}
                  className={field}
                >
                  <Text className={fieldSystem}>{system}</Text>
                </Field>
              )}
            </>
          )}
        </div>

        {indicatorPerformanceQuantities.length > 0 && (
          <div className={section}>
            <div className={section_indicatorPerformance_title}>
              <Text type="body1SemiBold">
                {t("project.events.drawer.create_manual_event.section.indicator_performance.title")}
              </Text>
              <TooltipV2
                placement="top"
                overlay={
                  <OverlayWrapper
                    text={t(
                      "project.events.drawer.create_manual_event.section.indicator_performance.title.tooltip",
                    )}
                  />
                }
              >
                <span>
                  <InformationLine size="s" color={color.carbon800} />
                </span>
              </TooltipV2>
            </div>
            <div className={section_indicatorPerformance_value}>
              {indicatorPerformanceQuantities.flatMap(q => {
                const impV = impactValues[q.id];
                if (impV === undefined) return [];

                return [
                  <>
                    <Field
                      key={q.id}
                      id={q.id}
                      label={`${q.name} ${q.unit ? `(${q.unit})` : ""}`}
                      className={classNames(field, fieldNumber)}
                    >
                      <SignedNumberInput
                        value={impV}
                        defaultNegative
                        onValueChange={value => {
                          setImpactValues(prev => ({
                            ...prev,
                            [q.id]: value,
                          }));
                        }}
                      />
                    </Field>
                  </>,
                ];
              })}
            </div>
          </div>
        )}
      </div>

      <div className={footer}>
        {(errorOverlappingManualFaultEvent || errorCreatingAnomaly) && (
          <div className={warningEditContainer}>
            <AlertFill size="s" color={color.phosphorus500} />
            <Text type="body1">
              {errorCreatingAnomaly
                ? t(
                    "project.events.drawer.create_manual_event.error.manual_fault_name_already_exists",
                  )
                : t(
                    "project.events.drawer.create_manual_event.error.overlapping_manual_fault_event",
                  )}
            </Text>
          </div>
        )}

        <div className={drawer_submit}>
          <div className={drawer_submit__mandatory}>
            <sup className={drawer_submit__sup}>*</sup> {tui("input.mandatory")}
          </div>
          <div className={drawer_submit__action}>
            <Button onClick={onCloseDrawer}> {t("commons.message_card.cancel")}</Button>
            <Button variant="primary" disabled={!isSubmittable} onClick={handleSubmit}>
              {t("commons.button.save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default DrawerCreateManualFaultEvent;

//#region CSS
const drawer = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
`;

const drawer_form = css`
  padding: 0 ${spacings.m} ${spacings.m} ${spacings.m};
  & > * + * {
    margin-top: ${spacings.m};
  }
`;
const section = css`
  padding-top: ${spacings.m};
  & > * + * {
    margin-top: ${spacings.m};
  }
`;
const section_anomaly_header = css`
  color: ${colors.carbon900};
  & > * + * {
    margin-top: ${spacings.xxs};
  }
  & > :nth-child(2) {
    color: ${color.carbon800};
  }
`;
const field = css`
  width: 100%;
  > div {
    border: unset;
    padding-left: 0;
    width: 100%;
    > div {
      width: 100%;

      div[class*="control"] {
        border-color: ${colors.titanium200};
      }
    }
  }
`;
const fieldNumber = css`
  > div > div {
    width: 86px;
  }
`;
const fieldSystem = css`
  color: ${colors.carbon800};
`;
const radioGroup = css`
  display: flex;
  gap: ${spacings.xs};
`;
const fieldWidth = css`
  width: 100%;
`;
const markdownWrapper = css`
  & > * + * {
    margin-top: ${spacings.xs};
  }
  color: ${colors.carbon800};
`;
const markdownEditorError = css`
  &.editable {
    &.error {
      border-color: ${colors.oxygen400};
    }
  }
`;
const markdownError = css`
  margin-top: ${spacings.xxs};
`;

const footer = css`
  position: sticky;
  bottom: 0;
`;

const warningEditContainer = css`
  display: flex;
  gap: ${spacings.xs};
  background-color: ${color.phosphorus50};
  padding: ${spacings.xs};
  border-top: 1px solid ${colors.titanium200};

  & > :first-child {
    flex-shrink: 0;
  }
  & > :last-child {
    color: ${colors.carbon800};
  }
`;

const section_indicatorPerformance_title = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${colors.carbon900};

  & > span svg {
    display: block;
  }
`;

const section_indicatorPerformance_value = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
`;

const drawer_submit = css`
  padding: ${spacings.m};
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${colors.titanium200};
  background-color: ${colors.titanium50};
`;
const drawer_submit__sup = css`
  color: ${colors.darkNitrogen600};
  vertical-align: bottom;
`;
const drawer_submit__mandatory = css`
  align-self: flex-end;
  font: ${fonts.body3};
  color: ${colors.darkNitrogen600};
`;
const drawer_submit__action = css`
  * + * {
    margin-left: ${spacings.xs};
  }
`;
// #endregion CSS
